import { css, jsx } from '@emotion/react';
import { useMemo } from 'react';
import { ToggleButton } from 'react-aria-components';
import { Typography, Link, Icon } from '@zapier/design-system';
import { useSecondaryNavContext } from './SecondaryNavContext.es.js';
import { SecondaryNavItemContext } from './SecondaryNavItemContext.es.js';

const styles = {
  navItem: css({
    "&:not(:last-child)": {
      marginBottom: "var(--zds-space-4, 0.25rem)"
    }
  }),
  navItemButton: ({ isSelected }) => css(Object.assign(Object.assign({}, Typography.Body1), {
    display: "flex",
    height: "var(--zds-size-medium, 2.25rem)",
    backgroundColor: isSelected ? "var(--zds-ui-brand-weakest, #fdf5f2)" : "transparent",
    // Using tokens results in a TypeScript error
    fontWeight: isSelected ? 600 : 400,
    "&:hover": {
      backgroundColor: isSelected ? "var(--zds-ui-brand-weakest, #fdf5f2)" : "var(--zds-background-stronger, #f5f3eb)"
    },
    "&:has([data-focus-visible],a:focus-visible)": {
      outline: "var(--zds-border-focus, 0.125rem solid #695be8)"
    },
    "ul ul &": {
      paddingLeft: "var(--zds-space-20, 1.25rem)"
    },
    "> *:last-child": {
      paddingRight: "var(--zds-space-16, 1rem)"
    },
    "& > *": {
      alignItems: "center",
      display: "flex",
      height: "100%",
      justifyContent: "space-between"
    }
  })),
  navItemLink: css({
    whiteSpace: "nowrap",
    flex: 1,
    paddingLeft: "var(--zds-space-16, 1rem)",
    color: "var(--zds-text-default, #413735)"
  }),
  navItemToggleButton: ({ isExpanded }) => css({
    paddingLeft: "var(--zds-space-16, 1rem)",
    "&:hover, &[data-focus-visible]": {
      color: "var(--zds-ui-primary-stronger, #695be8)"
    },
    '& > [class*="Icon"]': {
      transform: isExpanded ? "rotate(180deg) translateY(-1px)" : "unset"
    }
  })
};
const SecondaryNavItem = ({ children, className, label, linkProps, itemKey }) => {
  const { selectedItemKey, expandedSectionKeys, registerSelectedItemKey, registerExpandedSectionKeys } = useSecondaryNavContext();
  const isSelected = selectedItemKey === itemKey;
  const isExpanded = expandedSectionKeys.includes(itemKey);
  const onClick = (e) => {
    var _a;
    registerSelectedItemKey(itemKey);
    const dedupedKeys = /* @__PURE__ */ new Set([...expandedSectionKeys, itemKey]);
    registerExpandedSectionKeys(Array.from(dedupedKeys));
    (_a = linkProps.onClick) === null || _a === void 0 ? void 0 : _a.call(linkProps, e);
  };
  const onToggle = () => {
    registerExpandedSectionKeys(isExpanded ? expandedSectionKeys.filter((k) => k !== itemKey) : [...expandedSectionKeys, itemKey]);
  };
  const submenuId = `${itemKey}-menu`;
  const contextValue = useMemo(() => ({
    itemId: itemKey,
    listId: submenuId
  }), [itemKey, submenuId]);
  return jsx(
    SecondaryNavItemContext.Provider,
    { value: contextValue },
    jsx(
      "li",
      { className, css: styles.navItem, "data-zds": true },
      jsx(
        "div",
        { css: styles.navItemButton({ isSelected }), "data-zds": true },
        jsx(Link, Object.assign({}, linkProps, { "aria-current": isSelected ? "page" : void 0, css: styles.navItemLink, onClick }), label),
        children && jsx(
          ToggleButton,
          { "aria-controls": submenuId, "aria-expanded": isExpanded, "aria-label": isExpanded ? "Collapse menu" : "Expand menu", css: styles.navItemToggleButton({ isExpanded }), "data-zds": true, isSelected: isExpanded, onChange: onToggle },
          jsx(Icon, { name: "arrowSmallDown", size: 20, isBlock: true })
        )
      ),
      children
    )
  );
};

export { SecondaryNavItem };
//# sourceMappingURL=SecondaryNavItem.es.js.map
